// src/Context/PurchaseReauthContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

import {
  startPurchaseReauthEmailCode,
  verifyParentPurchaseCode,
} from "../Components/Utils/purchaseReauth.api";

import ParentApprovalRequiredModal from "../Components/Modals/ParentApprovalRequiredModal";
import VerifyEmailToPurchaseModal from "../Components/Modals/VerifyEmailToPurchaseModal";
import Modal from "../Components/Ui/Modal";

// ✅ IMPORTANT: render gating modals in Portal so they appear ABOVE other overlays
import Portal from "../Components/Utils/Portal";

type GateReason =
  | "EMAIL_REQUIRED"
  | "EMAIL_NOT_VERIFIED"
  | "GUARDIAN_LINK_REQUIRED"
  | "GUARDIAN_EMAIL_NOT_VERIFIED"
  | "GUARDIAN_EMAIL_CODE_REQUIRED"
  | "PURCHASES_DISABLED"
  | "GUARDIAN_MUST_VERIFY_IN_PARENT_PORTAL"
  | "UNKNOWN";

type UIState =
  | { kind: "closed" }
  | { kind: "verify_email"; initialEmail?: string }
  | { kind: "waiting_for_parent" }
  | { kind: "error"; title: string; message: string };

type RequireArgs = {
  context?: { sku?: string; source?: string };
  resume: () => Promise<void> | void;
};

type Pending = {
  resume: () => Promise<void> | void;
  context?: { sku?: string; source?: string };
};

type Ctx = {
  requirePurchaseReauth: (args: RequireArgs) => Promise<void>;
  close: () => void;
  retry: () => Promise<void>;

  // ✅ ParentApprovalRequiredModal needs this
  submitParentCode: (purchaseAuthId: string, code: string) => Promise<void>;
};

const PurchaseReauthContext = createContext<Ctx | null>(null);

function SimpleErrorModal({
  title,
  message,
  onClose,
}: {
  title: string;
  message: string;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      tone="dark"
      size="sm"
      eyebrow="Purchase"
      title={title}
      footer={
        <button type="button" className="mr-btn mr-btn--primary mr-btn--block" onClick={onClose}>
          OK
        </button>
      }
    >
      <p className="dlg-lead">{message}</p>
    </Modal>
  );
}

export function PurchaseReauthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [ui, setUI] = useState<UIState>({ kind: "closed" });

  // store the pending action to resume after success
  const resumeRef = useRef<null | (() => Promise<void> | void)>(null);

  // store pending context so we can retry cleanly
  const pendingRef = useRef<Pending | null>(null);

  const close = useCallback(() => {
    setUI({ kind: "closed" });
    resumeRef.current = null;
    pendingRef.current = null;
  }, []);

  const finishAndResume = useCallback(async () => {
    setUI({ kind: "closed" });

    const fn = resumeRef.current;
    resumeRef.current = null;
    pendingRef.current = null;

    if (fn) await fn();
  }, []);

  const requirePurchaseReauth = useCallback(
    async ({ resume, context }: RequireArgs) => {
      resumeRef.current = resume;
      pendingRef.current = { resume, context };

      try {
        const start = await startPurchaseReauthEmailCode();

        // If verified window exists, continue immediately (no modal)
        if (start?.alreadyAuthorized) {
          await finishAndResume();
          return;
        }

        // Student path -> parent approval modal
        if (start?.requiresGuardian) {
          setUI({ kind: "waiting_for_parent" });
          return;
        }

        // Adult path -> always use VerifyEmailToPurchaseModal now
        setUI({ kind: "verify_email" });
        return;
      } catch (err: any) {
        const status = err?.response?.status;
        const data = err?.response?.data || {};
        const reason = (data?.reason as GateReason) || "UNKNOWN";
        const message = String(data?.message || err?.message || "");

        // Adult missing email on account -> backend returns 403 EMAIL_REQUIRED
        if (status === 403 && reason === "EMAIL_REQUIRED") {
          setUI({ kind: "verify_email" });
          return;
        }

        // Student-related 403 reasons -> show parent modal
        if (
          status === 403 &&
          (reason === "GUARDIAN_LINK_REQUIRED" ||
            reason === "GUARDIAN_EMAIL_NOT_VERIFIED" ||
            reason === "GUARDIAN_EMAIL_CODE_REQUIRED" ||
            reason === "GUARDIAN_MUST_VERIFY_IN_PARENT_PORTAL")
        ) {
          setUI({ kind: "waiting_for_parent" });
          return;
        }

        setUI({
          kind: "error",
          title: "Purchase blocked",
          message: message || "Purchases are not available right now.",
        });
      }
    },
    [finishAndResume],
  );

  const retry = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) return;

    await requirePurchaseReauth({
      resume: pending.resume,
      context: pending.context,
    });
  }, [requirePurchaseReauth]);

  // ✅ called by ParentApprovalRequiredModal
  const submitParentCode = useCallback(
    async (purchaseAuthId: string, code: string) => {
      await verifyParentPurchaseCode(purchaseAuthId, code);
      await finishAndResume();
    },
    [finishAndResume],
  );

  return (
    <PurchaseReauthContext.Provider
      value={{ requirePurchaseReauth, close, retry, submitParentCode }}
    >
      {children}

      {ui.kind === "verify_email" && (
        <Portal>
          <VerifyEmailToPurchaseModal
            onClose={close}
            initialEmail={ui.initialEmail || ""}
            onVerified={finishAndResume}
          />
        </Portal>
      )}

      {ui.kind === "waiting_for_parent" && (
        <Portal>
          <ParentApprovalRequiredModal onClose={close} onRetry={retry} />
        </Portal>
      )}

      {ui.kind === "error" && (
        <Portal>
          <SimpleErrorModal
            title={ui.title}
            message={ui.message}
            onClose={close}
          />
        </Portal>
      )}
    </PurchaseReauthContext.Provider>
  );
}

export function usePurchaseReauth() {
  const ctx = useContext(PurchaseReauthContext);
  if (!ctx)
    throw new Error(
      "usePurchaseReauth must be used within PurchaseReauthProvider",
    );
  return ctx;
}
